/**
 * RecurrenceHistory — compact heatmap + controls for a recurring task in detail view.
 *
 * Collapsed: streak badge + chevron
 * Expanded: GitHub-style heatmap (shared HeatmapGrid) + Edit/Pause/Stop controls in header
 */

import { memo, useMemo, useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useLiveQueryImmediate } from "@/db/hooks"
import dayjs from "dayjs"
import { db, ObjectStatus } from "@/db/schema"
import type { DbObject, DbRecurrence } from "@/db/schema"
import { cn } from "@/shared/utils/general/cn"
import { pauseRecurrence, resumeRecurrence } from "@totodo/operations"
import { ChevronDown } from "lucide-react"
import PencilIcon from "@/shared/icons/pikaicons/solid/general/pencil-edit.svg?react"
import PauseIcon from "@/shared/icons/pikaicons/solid/media/pause-circle.svg?react"
import PlayIcon from "@/shared/icons/pikaicons/solid/media/play-circle.svg?react"
import LinkIcon from "@/shared/icons/pikaicons/stroke/development/link-horizontal.svg?react"
import { useRevealTask } from "@/features/search/useRevealTask"
import { RecurrenceBadge } from "./RecurrenceBadge"
import { HeatmapGrid, formatHeatmapDate } from "./HeatmapGrid"
import type { HeatmapCell } from "./HeatmapGrid"
import { parseDbDate } from "@totodo/operations"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RecurrenceHistoryProps {
  templateId: string
  recurrence: DbRecurrence
  onEditRecurrence?: () => void
  /** When true, shows a link icon to navigate to the recurring template */
  isInstance?: boolean
}

type Outcome = "completed" | "skipped" | "expired" | "deferred" | "unknown"

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const RecurrenceHistory = memo(function RecurrenceHistory({
  templateId,
  recurrence,
  onEditRecurrence,
  isInstance,
}: RecurrenceHistoryProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [hoveredCell, setHoveredCell] = useState<HeatmapCell | null>(null)
  const [weeks, setWeeks] = useState(8)

  // Recurrence start date — don't show "missed" days before this
  const recurrenceStart = useMemo(
    () => {
      const ms = parseDbDate(recurrence.startAt)
      return ms !== undefined ? dayjs(ms) : null
    },
    [recurrence.startAt],
  )

  // Fetch completed/canceled instances for this template
  const [instances] = useLiveQueryImmediate(
    () =>
      db.totodo
        .where("recurringTemplateId")
        .equals(templateId)
        .filter(
          (task) =>
            !task.isDeleted &&
            (task.status === ObjectStatus.Completed ||
              task.status === ObjectStatus.Canceled),
        )
        .toArray(),
    ["recurrenceInstances", templateId],
    [],
  )

  // Build a lookup from occurrenceKey → instance
  const instanceMap = useMemo(() => {
    const map = new Map<string, { outcome: Outcome; id: string }>()
    if (!instances || !Array.isArray(instances)) return map
    for (const inst of instances) {
      const key = inst.occurrenceKey ?? ""
      if (!key) continue
      map.set(key, {
        outcome: resolveOutcome(inst),
        id: inst.id ?? "",
      })
    }
    return map
  }, [instances])

  // Completion rate (last 30 days)
  const completionRate = useMemo(() => {
    if (!instances || !Array.isArray(instances) || instances.length === 0) return null
    const cutoff = dayjs().subtract(30, "day").startOf("day")
    let total = 0
    let completed = 0
    for (const inst of instances) {
      const key = inst.occurrenceKey
      if (!key || !/^\d{8}$/.test(key)) continue
      const d = dayjs(key, "YYYYMMDD")
      if (d.isBefore(cutoff)) continue
      total++
      if (inst.status === ObjectStatus.Completed) completed++
    }
    if (total === 0) return null
    return Math.round((completed / total) * 100)
  }, [instances])

  const todayStr = dayjs().format("YYYY-MM-DD")

  // Build heatmap grid in HeatmapCell format
  const grid = useMemo((): HeatmapCell[][] => {
    const today = dayjs()
    const todayDow = (today.day() + 6) % 7 // 0=Mon, 6=Sun
    const totalDays = (weeks - 1) * 7 + todayDow + 1
    const startDate = today.subtract(totalDays - 1, "day")
    // Only mark days as "missed" from recurrence start onward
    const activeFrom = recurrenceStart?.startOf("day") ?? today

    const result: HeatmapCell[][] = []
    let dayIndex = 0

    for (let w = 0; w < weeks; w++) {
      const week: HeatmapCell[] = []
      for (let d = 0; d < 7; d++) {
        if (dayIndex >= totalDays) {
          week.push({ date: "", colorClass: "", tooltip: "" })
        } else {
          const date = startDate.add(dayIndex, "day")
          const dateStr = date.format("YYYY-MM-DD")
          const occKey = date.format("YYYYMMDD")
          const inst = instanceMap.get(occKey)
          const beforeStart = date.isBefore(activeFrom)
          const outcome = inst?.outcome ?? (date.isAfter(today) || beforeStart ? null : "unknown")
          week.push({
            date: dateStr,
            colorClass: cellColor(outcome),
            tooltip: `${formatHeatmapDate(dateStr)} — ${outcomeLabel(outcome)}`,
            data: { outcome, instanceId: inst?.id },
          })
        }
        dayIndex++
      }
      result.push(week)
    }
    return result
  }, [instanceMap, weeks, recurrenceStart])

  const handleCellHover = useCallback((cell: HeatmapCell | null) => {
    setHoveredCell(cell)
  }, [])

  // Compute stats from grid
  const stats = useMemo(() => {
    let done = 0
    let total = 0
    for (const week of grid) {
      for (const cell of week) {
        if (!cell.date) continue
        const outcome = (cell.data as { outcome?: string } | undefined)?.outcome
        if (!outcome || outcome === "unknown") {
          // "unknown" = past day with no record = missed
          if (outcome === "unknown") total++
          continue
        }
        total++
        if (outcome === "completed") done++
      }
    }
    const rate = total > 0 ? Math.round((done / total) * 100) : 0
    return { done, total, rate }
  }, [grid])

  const isPaused = !!recurrence.pausedAt

  return (
    <div>
      {/* ── Header row: badge + controls (when expanded) + chevron ── */}
      <div className="flex min-h-8 items-center gap-0.5 py-1">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="group flex min-w-0 items-center gap-2 rounded-md transition-colors hover:bg-muted/50"
        >
          <RecurrenceBadge recurrence={recurrence} completionRate={completionRate} />
          <ChevronDown
            className={cn(
              "size-3.5 shrink-0 opacity-40 transition-transform group-hover:opacity-70",
              expanded && "rotate-180",
            )}
          />
        </button>

        {expanded && (
          <div className="ml-auto flex items-center gap-1">
            {/* Link to template task (shown on instances only) */}
            {isInstance && <GoToTemplateButton templateId={templateId} />}
            {onEditRecurrence && (
              <button
                type="button"
                onClick={onEditRecurrence}
                className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground/60 transition-colors hover:bg-muted/50 hover:text-foreground"
              >
                <PencilIcon className="size-3" />
                <span>{t("Edit")}</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (isPaused) {
                  void resumeRecurrence(recurrence.id)
                } else {
                  void pauseRecurrence(recurrence.id)
                }
              }}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground/60 transition-colors hover:bg-muted/50 hover:text-foreground"
            >
              {isPaused ? <PlayIcon className="size-3" /> : <PauseIcon className="size-3" />}
              <span>{isPaused ? t("Resume") : t("Pause")}</span>
            </button>
          </div>
        )}
      </div>

      {/* ── Expanded: heatmap ──────────────────────────────────────── */}
      {expanded && (
        <div className="pb-1">
          <HeatmapGrid
            grid={grid}
            onWeeksChange={setWeeks}
            today={todayStr}
            interactive
            onCellHover={handleCellHover}
            maxWeeks={52}
            legend={
              hoveredCell?.date ? (
                <div className="flex w-full items-center justify-between">
                  <span>
                    {hoveredCell.tooltip}
                  </span>
                  {stats.total > 0 && (
                    <span className="opacity-50">{stats.done}/{stats.total} · {stats.rate}%</span>
                  )}
                </div>
              ) : (
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <div className="h-[8px] w-[8px] rounded-[2px] bg-green-500 dark:bg-green-400" />
                    <span>Done</span>
                    <div className="ml-1 h-[8px] w-[8px] rounded-[2px] bg-yellow-400 dark:bg-yellow-500" />
                    <span>Skip</span>
                    <div className="ml-1 h-[8px] w-[8px] rounded-[2px] bg-foreground/10" />
                    <span>Miss</span>
                  </div>
                  {stats.total > 0 && (
                    <span className="opacity-50">{stats.done}/{stats.total} · {stats.rate}%</span>
                  )}
                </div>
              )
            }
          />
        </div>
      )}
    </div>
  )
})

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resolveOutcome(inst: DbObject): Outcome {
  if (inst.instanceOutcome) return inst.instanceOutcome as Outcome
  if (inst.status === ObjectStatus.Completed) return "completed"
  if (inst.status === ObjectStatus.Canceled) return "skipped"
  return "unknown"
}

function cellColor(outcome: Outcome | null): string {
  switch (outcome) {
    case "completed":
      return "bg-green-500 dark:bg-green-400"
    case "skipped":
      return "bg-yellow-400 dark:bg-yellow-500"
    case "expired":
      return "bg-red-400 dark:bg-red-500"
    case "deferred":
      return "bg-blue-400 dark:bg-blue-500"
    case "unknown":
      return "bg-foreground/10"
    case null:
      return "bg-muted"
    default:
      return "bg-muted"
  }
}

function outcomeLabel(outcome: Outcome | null): string {
  switch (outcome) {
    case "completed": return "Completed"
    case "skipped": return "Skipped"
    case "expired": return "Expired"
    case "deferred": return "Deferred"
    case "unknown": return "—"
    default: return ""
  }
}

// ---------------------------------------------------------------------------
// GoToTemplateButton — navigate to the recurring template task
// ---------------------------------------------------------------------------

const GoToTemplateButton = memo(function GoToTemplateButton({
  templateId,
}: {
  templateId: string
}) {
  const { t } = useTranslation()
  const revealTask = useRevealTask()

  return (
    <button
      type="button"
      onClick={() => revealTask(templateId)}
      title={t("recurrence.goToTemplate", "Go to recurring template")}
      className="text-muted-foreground/50 transition-colors hover:text-muted-foreground"
    >
      <LinkIcon className="size-4" />
    </button>
  )
})
